/**
 * 날짜 필터를 **연 › 월 › 일** 로 묶는다 (엑셀 날짜 필터와 같은 모양).
 *
 * 왜 필요한가 — 날짜 컬럼은 고유값이 수백 개라 평평한 체크박스 목록으로는 못 고른다.
 * 「2025년만」·「2026년 3월만」을 한 번에 고를 수 있어야 한다.
 *
 * ⭐저장되는 필터 값은 **바뀌지 않는다** — 트리는 화면 표현일 뿐이고,
 *   체크 결과는 지금까지와 똑같이 `raw 값 문자열의 Set` 으로 담긴다.
 *   그래서 applyColState 는 손댈 필요가 없다.
 *
 * ⚠하루에 raw 값이 여러 개일 수 있다 ('2026-08-10' 과 '2026-08-10 14:30' 처럼).
 *   그래서 '일' 마디가 곧 raw 값 하나가 아니라 **raw 값 묶음**이다 — 일을 켜면 그 날의 값이 전부 켜진다.
 *
 * 날짜로 못 읽는 값(빈 값·'미정' 같은 글자)은 버리지 않고 `others` 로 따로 내려보내
 * 목록 아래쪽에 평범한 체크박스로 보여 준다. **조용히 사라지면 안 된다.**
 */

/** 하루 — 그 날에 속한 raw 값 전부 */
export interface DayNode {
  day: number;
  label: string;
  values: string[];
}

export interface MonthNode {
  month: number;
  label: string;
  days: DayNode[];
  /** 이 달에 속한 raw 값 전부 (연·월 단위 토글용) */
  values: string[];
}

export interface YearNode {
  year: number;
  label: string;
  months: MonthNode[];
  values: string[];
}

export interface DateTree {
  years: YearNode[];
  /** 날짜로 못 읽은 값들 (빈 값 포함) — 평평하게 보여 준다 */
  others: string[];
}

/**
 * 'YYYY-MM-DD' 를 기본으로, 흔한 구분자(. /)와 뒤에 붙은 시각도 받아 준다.
 * 못 읽으면 null — 호출부가 `others` 로 보낸다.
 */
export function parseDateParts(raw: string): { y: number; m: number; d: number } | null {
  const s = raw.trim();
  if (s === '') return null;
  const m = /^(\d{4})[-./](\d{1,2})[-./](\d{1,2})/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return { y, m: mo, d };
}

/** raw 값 목록 → 연›월›일 트리. 연·월·일 모두 오름차순. */
export function buildDateTree(values: string[]): DateTree {
  const years = new Map<number, Map<number, Map<number, string[]>>>();
  const others: string[] = [];

  for (const v of values) {
    const p = parseDateParts(v);
    if (!p) {
      others.push(v);
      continue;
    }
    let ms = years.get(p.y);
    if (!ms) { ms = new Map(); years.set(p.y, ms); }
    let ds = ms.get(p.m);
    if (!ds) { ds = new Map(); ms.set(p.m, ds); }
    const list = ds.get(p.d);
    if (list) list.push(v);
    else ds.set(p.d, [v]);
  }

  const num = (a: number, b: number) => a - b;
  const yearNodes: YearNode[] = [...years.keys()].sort(num).map((y) => {
    const ms = years.get(y)!;
    const monthNodes: MonthNode[] = [...ms.keys()].sort(num).map((mo) => {
      const ds = ms.get(mo)!;
      const dayNodes: DayNode[] = [...ds.keys()].sort(num).map((d) => ({
        day: d,
        label: `${d}일`,
        values: ds.get(d)!,
      }));
      return {
        month: mo,
        label: `${mo}월`,
        days: dayNodes,
        values: dayNodes.flatMap((n) => n.values),
      };
    });
    return {
      year: y,
      label: `${y}년`,
      months: monthNodes,
      values: monthNodes.flatMap((n) => n.values),
    };
  });

  // 날짜 아닌 값도 보기 좋게 — 빈 값은 맨 앞
  others.sort((a, b) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b, 'ko')));

  return { years: yearNodes, others };
}

/** 묶음이 전부 켜졌는가 / 일부만 켜졌는가 */
export function groupCheckState(
  values: string[],
  selected: Set<string>,
): { all: boolean; some: boolean } {
  if (values.length === 0) return { all: false, some: false };
  let on = 0;
  for (const v of values) if (selected.has(v)) on += 1;
  return { all: on === values.length, some: on > 0 && on < values.length };
}
